import { useState } from 'react';
import {
  JOIN_REQUEST_ORIGIN_LABEL,
  JOIN_REQUEST_STATUS_LABELS,
  type JoinRequestRow,
  type JoinRequestStatus,
} from '@scoutaround/script-breakdown-shared';

import { Callout } from '../Callout.js';
import { StatusPill, type WorkState } from '../ui/StatusPill.js';
import { SideDialog } from './SideDialog.js';
import { RoleSelect, formatDay, initialsOf, roleLabel, roleMeaning } from './roles.js';

/** Each request state in the workspace's one status vocabulary. */
export const JOIN_REQUEST_PILL: Readonly<Record<JoinRequestStatus, WorkState>> = {
  PENDING_APPROVAL: 'READY_FOR_REVIEW',
  APPROVED_AWAITING_ACTIVATION: 'IN_PROGRESS',
  APPROVED: 'COMPLETED',
  REJECTED: 'EXCLUDED',
};

/** A requester as the team reads them: the platform's name, or their number while it cannot be read. */
export function requesterName(request: JoinRequestRow): string {
  return request.displayName ?? `Person #${String(request.userId)}`;
}

/**
 * JOIN REQUESTS — people who asked through the invitation link (owner brief
 * 2026-11-02, §4).
 *
 * Who asked (the platform's name and address, and whether the address is
 * verified), when, from where, where it stands. Approve and Reject on a pending
 * request, for the production's OWNER only — everyone else sees why not, and the
 * server refuses them anyway. Rows never carry a primary button: the page keeps
 * its one red "Invite teammate".
 */
export function JoinRequestList({
  requests,
  canDecide,
  refusal,
  onApprove,
  onReject,
}: {
  requests: readonly JoinRequestRow[];
  canDecide: boolean;
  refusal: string | null;
  onApprove: (request: JoinRequestRow) => void;
  onReject: (request: JoinRequestRow) => void;
}) {
  return (
    <ul className="team-invite-list" data-testid="join-request-list">
      {requests.map((r) => {
        const pending = r.status === 'PENDING_APPROVAL';
        return (
          <li key={r.id} className="team-request" data-state={r.status} data-testid={`join-request-${r.id}`}>
            <span className="team-avatar" aria-hidden="true">
              {initialsOf(r.displayName)}
            </span>
            <div className="team-invite-main">
              <div className="team-invite-line">
                <span className="team-invite-email" data-testid={`join-request-name-${r.id}`}>
                  {requesterName(r)}
                </span>
                <StatusPill
                  state={JOIN_REQUEST_PILL[r.status]}
                  label={JOIN_REQUEST_STATUS_LABELS[r.status]}
                  size="sm"
                  testId={`join-request-status-${r.id}`}
                />
              </div>
              {r.email === null ? null : (
                <span className="team-request-email" data-testid={`join-request-email-${r.id}`}>
                  {r.email}
                  <span
                    className="team-request-verified"
                    data-verified={r.emailVerified === true ? 'true' : 'false'}
                    data-testid={`join-request-verified-${r.id}`}
                  >
                    {r.emailVerified === true ? 'Verified email' : 'Email not verified'}
                  </span>
                </span>
              )}
              <span className="team-invite-when" data-testid={`join-request-when-${r.id}`}>
                Requested {formatDay(r.requestedAt)} · {JOIN_REQUEST_ORIGIN_LABEL}
                {r.authorizationRole === null ? '' : ` · ${roleLabel(r.authorizationRole)}`}
              </span>
              {pending && r.accountState !== null && r.accountState !== 'ACTIVE' ? (
                <span className="team-request-flag" data-testid={`join-request-account-${r.id}`}>
                  {r.accountState === 'SUSPENDED' ? 'This account is suspended on ScoutAround.' : 'This account no longer exists.'}
                </span>
              ) : null}
            </div>
            {pending ? (
              <div className="team-invite-actions">
                <button
                  type="button"
                  className="ctl"
                  data-testid={`join-request-approve-${r.id}`}
                  disabled={!canDecide}
                  title={refusal ?? undefined}
                  onClick={() => {
                    onApprove(r);
                  }}
                >
                  Approve
                </button>
                <button
                  type="button"
                  className="ctl team-danger"
                  data-testid={`join-request-reject-${r.id}`}
                  disabled={!canDecide}
                  title={refusal ?? undefined}
                  onClick={() => {
                    onReject(r);
                  }}
                >
                  Reject
                </button>
              </div>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * APPROVE WITH A ROLE — the compact confirmation (brief §4). The owner picks the
 * job from the catalogue; the server re-checks everything: that they still own
 * the production, that the request is still pending, and the requester's account
 * as ScoutAround says it is now.
 */
export function ApproveJoinDialog({
  request,
  pending,
  error,
  onApprove,
  onClose,
}: {
  request: JoinRequestRow;
  pending: boolean;
  error: string | null;
  onApprove: (role: string) => void;
  onClose: () => void;
}) {
  // The most common hire on this product's surface, and a safe one — the invite form's default.
  const [role, setRole] = useState('LOCATION_MANAGER');

  return (
    <SideDialog title="Approve request" testId="approve-join-dialog" onClose={onClose}>
      <p className="team-dialog-lede" data-testid="approve-join-who">
        {requesterName(request)}
        {request.email === null ? '' : ` (${request.email})`} asked to join through your invitation link. Choose their
        role on this production.
      </p>
      <form
        className="team-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (!pending) onApprove(role);
        }}
      >
        <div className="team-field">
          <label className="flbl" htmlFor="approve-join-role">
            Role on the production
          </label>
          <RoleSelect id="approve-join-role" testId="approve-join-role" value={role} onChange={setRole} startHere />
          <p className="team-field-help" data-testid="approve-join-meaning">
            {roleMeaning(role)}
          </p>
        </div>
        {request.workspaceEnabled === false ? (
          <p className="team-field-help" data-testid="approve-join-activation">
            ScoutAround has not enabled the Production workspace for this account yet. They are approved now and join
            the team as soon as it is enabled.
          </p>
        ) : null}
        {error === null ? null : (
          <Callout tone="danger" title="Not approved" testId="approve-join-error">
            {error}
          </Callout>
        )}
        <div className="team-dialog-actions">
          <button type="submit" className="btn btn-primary" data-testid="approve-join-submit" disabled={pending}>
            {pending ? 'Approving…' : 'Approve and add'}
          </button>
          <button type="button" className="ctl" data-testid="approve-join-cancel" onClick={onClose}>
            Cancel
          </button>
        </div>
      </form>
    </SideDialog>
  );
}

/** REJECT — said plainly, with what follows: an email to them, and the link working for everyone else. */
export function RejectJoinDialog({
  request,
  pending,
  error,
  onReject,
  onClose,
}: {
  request: JoinRequestRow;
  pending: boolean;
  error: string | null;
  onReject: () => void;
  onClose: () => void;
}) {
  return (
    <SideDialog title="Reject request" testId="reject-join-dialog" onClose={onClose}>
      <p className="team-dialog-lede" data-testid="reject-join-who">
        Reject {requesterName(request)}’s request to join? They are told by email and get no access. The invitation
        link keeps working for everyone else.
      </p>
      {error === null ? null : (
        <Callout tone="danger" title="Not rejected" testId="reject-join-error">
          {error}
        </Callout>
      )}
      <div className="team-dialog-actions">
        <button
          type="button"
          className="btn btn-primary"
          data-testid="reject-join-submit"
          data-autofocus
          disabled={pending}
          onClick={onReject}
        >
          {pending ? 'Rejecting…' : 'Reject request'}
        </button>
        <button type="button" className="ctl" data-testid="reject-join-cancel" onClick={onClose}>
          Cancel
        </button>
      </div>
    </SideDialog>
  );
}
